import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { isCompleteStatus } from '../utils/reportHelpers';
import TwoZoneUpload from '../components/TwoZoneUpload';
import FindingsViewer from '../components/FindingsViewer';
import { useReports } from '../context/ReportContext';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import './PolicyChecker.css';

export default function PolicyChecker() {
  const [view, setView] = useState('upload');
  const [isComparing, setIsComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [banner, setBanner] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const runIdParam = searchParams.get('runId');
  const loadedRunIdRef = useRef(null);

  const { reports, addReport, removeReport, refreshReports } = useReports();

  // Refresh reports on mount so a run triggered from Library appears immediately
  useEffect(() => {
    refreshReports();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When a triggered comparison completes, switch the in-progress toast to the ready toast
  useEffect(() => {
    if (!comparison?.runIds?.length || view !== 'upload') return;
    const runId = comparison.runIds[0];
    const report = reports.find((r) => r.id === runId);
    if (report && isCompleteStatus(report.status)) {
      setBanner((prev) => {
        if (prev?.isReady) return prev;
        if (!prev?.isInfo) return prev;
        return { isReady: true, runId };
      });
    }
  }, [reports, comparison, view]);

  // Track previous library_audit run statuses to detect completion
  const libraryRunStatusesRef = useRef({});
  useEffect(() => {
    if (view !== 'upload') return;
    for (const r of reports) {
      if (r.comparisonType !== 'library_audit') continue;
      const prev = libraryRunStatusesRef.current[r.id];
      libraryRunStatusesRef.current[r.id] = r.status;
      if (prev && !isCompleteStatus(prev) && isCompleteStatus(r.status)) {
        setBanner((b) => b?.isReady ? b : { isReady: true, runId: r.id });
      }
    }
  }, [reports, view]);

  const reportsRef = useRef(reports);
  useEffect(() => { reportsRef.current = reports; }, [reports]);

  const { user, loading: authLoading } = useAuth();
  const { triggerScan, fetchJobStatus } = useApi();

  useEffect(() => {
    if (!runIdParam || authLoading) return;
    if (loadedRunIdRef.current === runIdParam) return;
    loadedRunIdRef.current = runIdParam;

    const reportFromContext = reportsRef.current.find((r) => r.id === runIdParam);
    if (reportFromContext && reportFromContext.comparisonType && reportFromContext.comparisonType !== 'library_audit') {
      setComparison({
        runIds: [runIdParam],
        primaryDocName: reportFromContext.primaryDocName || runIdParam,
        refDocNames: [reportFromContext.referenceDocName].filter(Boolean),
        type: reportFromContext.comparisonType || 'targeted',
      });
      setView('results');
      return;
    }

    fetchJobStatus(runIdParam)
      .then((status) => {
        const isLibraryAudit = status.comparison_type === 'library_audit';
        setComparison({
          runIds: [runIdParam],
          primaryDocName: isLibraryAudit ? 'Library Comparison' : (status.filename_a || runIdParam),
          refDocNames: isLibraryAudit ? [] : (status.filenames_b || ([status.filename_b].filter(Boolean))),
          type: status.comparison_type || 'targeted',
        });
        setView('results');
      })
      .catch(() => {
        loadedRunIdRef.current = null;
        setView('upload');
      });
  }, [runIdParam, authLoading, fetchJobStatus]);

  const handleCompare = async ({ primaryFile, referenceFiles, isSelf }) => {
    if (isComparing) return;
    setIsComparing(true);

    const primaryDocName = primaryFile?.name || 'Document';
    const refDocNames = isSelf ? [] : referenceFiles.map((f) => f.name);

    if (!primaryFile?.policyId || (!isSelf && referenceFiles.some((f) => !f.policyId))) {
      setIsComparing(false);
      setBanner({ message: 'One or more documents is missing a policy ID. Please re-upload and try again.', isError: true });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    addReport({
      id: tempId,
      type: 'Comparison',
      status: 'RUNNING',
      createdAt: new Date().toISOString(),
      createdBy: user?.email || user?.name,
    });

    try {
      const result = isSelf
        ? await triggerScan(primaryFile.policyId, null, primaryFile.name, null, { comparisonType: 'self' })
        : await triggerScan(
            primaryFile.policyId,
            referenceFiles.map((f) => f.policyId),
            primaryFile.name,
            referenceFiles.map((f) => f.name),
          );
      if (result.status === 'provisions_not_ready') {
        removeReport(tempId);
        setBanner({ message: 'Document provisions are not ready yet. Please try again in a few moments.', isError: true });
        setIsComparing(false);
        return;
      }
      const runIds = [result.run_id].filter(Boolean);
      if (!runIds.length) throw new Error('No run ID returned from scan.');

      removeReport(tempId);
      runIds.forEach((runId) => {
        addReport({
          id: runId,
          type: 'Comparison',
          status: 'RUNNING',
          createdAt: new Date().toISOString(),
          createdBy: user?.email || user?.name,
        });
      });

      setComparison({ runIds, primaryDocName, refDocNames, type: isSelf ? 'self' : 'targeted' });
      setBanner({ title: 'Comparison in progress', message: "We're processing your request. The results will appear shortly.", isInfo: true });
      setResetKey((k) => k + 1);
    } catch (err) {
      removeReport(tempId);
      console.error('Comparison failed:', err);
      setBanner({ message: 'Comparison failed. Please try again.', isError: true });
    } finally {
      setIsComparing(false);
    }
  };

  if (runIdParam && authLoading) {
    return <div className="pc-loading" aria-busy="true">Loading...</div>;
  }

  if (view === 'results' && comparison) {
    return (
      <div className="policy-checker-page">
        {banner && banner.isError && (
          <div className="comparison-ready-banner error">
            <p>{banner.message}</p>
            <button className="banner-dismiss" onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        <div className="pc-results-view">
          <FindingsViewer
            runIds={comparison.runIds}
            primaryDocName={comparison.primaryDocName}
            referenceDocNames={comparison.refDocNames}
            comparisonType={comparison.type}
            onBack={() => { setView('upload'); setComparison(null); setBanner(null); loadedRunIdRef.current = null; setSearchParams({}); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="policy-checker-page">
      {banner && banner.isError && (
        <div className="comparison-ready-banner error">
          <p>{banner.message}</p>
          <button className="banner-dismiss" onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {banner && banner.isInfo && (
        <div className="pc-toast">
          <span className="pc-toast-icon">ⓘ</span>
          <div className="pc-toast-text">
            <strong>{banner.title}</strong>
            <p className="pc-toast-body">{banner.message}</p>
          </div>
          <button className="pc-toast-close" onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {banner && banner.isReady && (
        <div className="pc-ready-toast">
          <span className="pc-toast-icon">ⓘ</span>
          <div className="pc-toast-text">
            <strong>Your comparison is ready</strong>
            <p className="pc-toast-body">Your Policy Checker comparison is ready to view.</p>
          </div>
          <button className="banner-view-btn" onClick={() => navigate(`/policy-checker?runId=${banner.runId}`)}>View</button>
          <button className="pc-toast-close" onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="policy-checker-content">
        <TwoZoneUpload key={resetKey} onCompare={handleCompare} isComparing={isComparing} />
      </div>
    </div>
  );
}
